import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { find, isEmpty, omit, some, toString, isNaN } from 'lodash';
import { Grid, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { getRawValue, getStringValue } from 'common/util/dataTypeValues';
import { Conditional } from 'common/components/layout';
import GoBack from 'components/common/Link/GoBack';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import { dataSourceDataTypeSelector, dataSourceSelector, isSubmittingSelector } from 'pages/dataSource/selectors';
import {
    loadDataSource,
    unloadDataSource,
    saveDataSource,
    writeDataSourceValues,
    loadDataSourceLog,
    downloadDataSource
} from 'pages/dataSource/actions/dataSource';
import FormDataSourceAddNew from '../components/DataSourceAddNew';
import DownloadDataSource from '../components/DownloadDataSource';
import DataSourceGrid from '../components/DataSourceGrid';
import DialogDataSource from '../components/DialogDataSource';
import { dataSourceLogActions, dataSourceStatus } from '../utils/constants';
import styles from './DataSourceContainer.scss';

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        overflowX: 'hidden',
        overflowY: 'auto',
        paddingBottom: '1rem',
    },
    paper: {
        padding: theme.spacing(1.5),
        boxShadow: 'none',
    },
}));

const DataSourceContainer = ({
    params: { dataSourceId },
    dataSource,
    dataType,
    isSubmitting,
    addApplicationMessage,
    loadDataSource,
    unloadDataSource,
    writeDataSourceValues,
    saveDataSource,
    loadDataSourceLog,
    downloadDataSource
}) => {
    const classes = useStyles();
    const [dialogDataSource, setDialogDataSource] = useState(false);
    const [addNewDataSourceOpen, setAddNewDataSourceOpen] = useState(false);
    const [rowSelected, setRowSelected] = useState(null);
    const [addNewValue, setAddNewValue] = useState('');
    const dataSourceValues = dataSource && dataSource.parameters ? dataSource.parameters.values : [];

    useEffect(() => {
        loadDataSource(dataSourceId);

        return () => unloadDataSource();
    }, []);

    const toggleDialogDataSource = () => {
        setDialogDataSource(!dialogDataSource);
    };

    const toggleAddNewDataSource = () => {
        if (isSubmitting) return;
        setAddNewDataSourceOpen(!addNewDataSourceOpen);
        setAddNewValue('');
    };

    const onChangeAddNewDataSource = e => {
        setAddNewValue(toString(e.target.value));
    };

    const onClickChangeStatus = rowData => {
        setRowSelected(rowData);
        toggleDialogDataSource();
    };

    const determineValueForDataType = value => {
        const rawTypesNotAllowed = ['DATEONLY', 'DATETIME'];
        if (rawTypesNotAllowed.includes(dataType)) {
            return getStringValue(dataType, value);
        }
        return getRawValue(dataType, value);
    };

    const getErrorMessage = value => {
        let message = '';

        if (some(dataSourceValues, { value })) {
            message = 'This item already exists in the list. You may not add the same item twice.';
        }
        if (isNaN(value)) {
            message = `Invalid data type. Please add an item with ${dataType} data type for this data source.`;
        }

        return message;
    };

    const addNewDataSourceValue = () => {
        if (!addNewValue.trim()) return;

        const value = determineValueForDataType(addNewValue);
        const message = getErrorMessage(value);
        if (message) {
            return addApplicationMessage('error', message);
        }

        const dataSourceItem = {
            value,
            text: getStringValue(dataType, addNewValue),
            status: dataSourceStatus.active.value,
            order: dataSourceValues.length + 1,
            dataType,
        };
        const newDataSourceValues = [...dataSourceValues, dataSourceItem];

        toggleAddNewDataSource();
        setAddNewValue('');
        handleSaveDataSource(dataSourceLogActions.created, dataSourceItem, newDataSourceValues);
    };

    const updateDataSourceStatus = () => {
        const findItem = find(dataSourceValues, ['value', rowSelected.value]);
        if (!findItem) return;

        const isInactive = rowSelected.status === dataSourceStatus.inactive.value;
        const action = isInactive ? dataSourceLogActions.restored : dataSourceLogActions.deactivated;
        findItem.status = isInactive ? dataSourceStatus.active.value : dataSourceStatus.inactive.value;
        const dataSourceItem = omit(findItem, ['tableData']);
        handleSaveDataSource(action, dataSourceItem, dataSourceValues);
    };

    const handleSaveDataSource = (action, dataSourceItem, dataSourceValues) => {
        const { id: dataSourceMetadataId, institutionId, name, description, type } = dataSource;
        const dataSourceLog = {
            ...dataSourceItem,
            value: String(dataSourceItem.value),
            action,
            dataSourceMetadataId,
            institutionId,
            name,
            description,
            type,
        };

        loadDataSourceLog(dataSourceLog);
        writeDataSourceValues(dataSourceValues);
        saveDataSource(institutionId);
    };

    if (!dataSource) {
        return <LoadingStatus loading={true} filterQuery={false} />;
    }

    return (
        <div className={classes.root} data-cy="DataSourceContainer">
            <Grid container>
                <Grid item xs={12}>
                    <Paper className={classes.paper}>
                        <GoBack to={'/admin/datasources'} text={'Back to Data Sources'} />
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <h1 className={styles.DataSource__Title} data-cy="DataSourceContainer-name">{dataSource.name}</h1>
                    <p>
                        This list reflects the order of the Data Source menu as it will appear across the application.
                    </p>
                </Grid>

                <Grid item xs={12} lg={8} className={styles.DataSourceAddNew}>
                    <FormDataSourceAddNew
                        addNewDataSourceOpen={addNewDataSourceOpen}
                        addNewValue={addNewValue}
                        toggleAddNewDataSource={toggleAddNewDataSource}
                        onChangeAddNewDataSource={onChangeAddNewDataSource}
                        addNewDataSourceValue={addNewDataSourceValue}
                        dataType={dataType}
                    />

                    <DownloadDataSource onDownloadDataSource={downloadDataSource} />                 
                </Grid>

                <Grid item xs={12} lg={8} className={styles.DataSource}>
                    <DataSourceGrid data={dataSourceValues} onClickChangeStatus={onClickChangeStatus} />
                </Grid>
            </Grid>

            <Conditional condition={!isEmpty(rowSelected) && !isSubmitting}>
                <DialogDataSource
                    dialogOpen={dialogDataSource}
                    rowSelected={rowSelected}
                    toggleDialog={toggleDialogDataSource}
                    onConfirm={updateDataSourceStatus}
                />
            </Conditional>
        </div>
    );
};

const mapStateToProps = state => ({
    dataSource: dataSourceSelector(state),
    dataType: dataSourceDataTypeSelector(state),
    isSubmitting: isSubmittingSelector(state),
});

const mapDispatchToProps = {
    addApplicationMessage,
    loadDataSource,
    unloadDataSource,
    writeDataSourceValues,
    saveDataSource,
    loadDataSourceLog,
    downloadDataSource
};

export { DataSourceContainer };
export default connect(mapStateToProps, mapDispatchToProps)(DataSourceContainer);
